import React from 'react';
import PageShell from '../components/PageShell';
import SectionTitle from '../components/SectionTitle';
import Icon from '../components/Icon';
import Eyebrow from '../components/page/Eyebrow';
import InlineCta from '../components/page/InlineCta';
import FaqList from '../components/page/FaqList';
import PageSection from '../components/page/PageSection';
import { tie } from '../components/page/typography';
import LaunchRoute from '../components/launch/LaunchRoute';
import LaunchStages from '../components/launch/LaunchStages';
import { launchStages } from '../content/services';
import { launchPlanCopy } from '../content/pages';

/*
 * Другий абзац вступу розкладено по місцях, де він працює: застереження про
 * нежорсткий графік — під самим шляхом, бо стосується саме його, а думка
 * про найдорожчі помилки — поруч із першим абзацом, окремою карткою.
 * Речення «Нижче — той самий шлях…» відпало: шлях тепер і є перше, що
 * видно під банером.
 */
const routeNote =
  'Це не жорсткий графік: частина кроків іде паралельно, а частина відпадає, якщо у вас уже є зареєстрований суб’єкт або приміщення. Але порядок залежностей саме такий.';

const LaunchPlanPage = () => {
  const howToSchema = {
    '@type': 'HowTo',
    name: 'Як відкрити клініку в Україні: план запуску',
    description:
      'Шість етапів відкриття медичного закладу — від вибору організаційно-правової форми до отримання ліцензії МОЗ і початку роботи.',
    step: launchStages.map((stage, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: stage.title,
      itemListElement: stage.items.map((item) => ({
        '@type': 'HowToDirection',
        text: `${item.label}: ${item.text}`
      }))
    }))
  };

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: launchPlanCopy.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  };

  return (
    <PageShell
      title="Як відкрити клініку: план за 6 етапів"
      description="Як відкрити медичний заклад в Україні: вибір форми та КВЕДів, аудит приміщення за ДБН, кадри, оснащення, отримання ліцензії МОЗ і старт роботи в ЕСОЗ."
      path="/plan-zapusku-kliniky"
      banner="/images/page-docs-d84abdfc.jpg"
      schema={[howToSchema, faqSchema]}
      crumbs={[{ name: 'План запуску клініки' }]}
      heading="Відкриття клініки під ключ за 6 етапів"
      lead="Від ідеї до першого пацієнта. Кожен етап — з переліком того, що саме робиться, щоб ви бачили весь шлях наперед, а не дізнавалися про вимоги в останній момент."
      cta={{ source: 'launch-plan' }}
    >
      <div className="page-container py-10 lg:py-14">
        <LaunchRoute stages={launchStages} note={routeNote} />

        {/* Розділ мав лише підпис, а не заголовок: «Чому саме такий
            порядок» стояло дрібними літерами, і на тлі сусідніх сторінок
            здавалося, що заголовок загубився. Тепер — як усі розділи сайту:
            підпис, h2 і стандартний відступ PageSection. Лід ліворуч,
            висновок із нього — праворуч карткою, щоб його не пропустили. */}
        <PageSection id="poryadok" caption="Логіка запуску" title="Чому саме такий порядок">
          <div className="grid gap-6 lg:grid-cols-12 lg:gap-10 xl:gap-14 lg:items-center">
            <p className="lg:col-span-6 xl:col-span-7 text-lg lg:text-xl leading-relaxed text-slate-800 border-l-2 border-teal-400 pl-5 lg:pl-6">
              {tie(launchPlanCopy.intro[0])}
            </p>
            {/* Плитка іконки бурштинова, як попередження на сторінках
                послуг і в статтях: бірюзовий трикутник читався як звичайна
                примітка, а не як «обережно». */}
            <div className="lg:col-span-6 xl:col-span-5 flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 lg:p-7">
              <span className="flex-shrink-0 w-10 h-10 rounded-xl border border-amber-200 bg-amber-50 text-amber-700 flex items-center justify-center" aria-hidden="true">
                <Icon name="fa-exclamation-triangle" />
              </span>
              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                {tie('Тому ')}
                <strong className="font-semibold text-slate-900">
                  {tie('найдорожчі помилки на старті — не юридичні, а порядкові')}
                </strong>
                {tie(': оренда, підписана до аудиту приміщення, або ремонт, зроблений до того, як стало ясно, скільки кабінетів і якої площі потрібно.')}
              </p>
            </div>
          </div>
        </PageSection>

        {/* Без свого h2: кожен етап нижче сам є розділом із h2, і заголовок
            над ними лише повторив би підпис. PageSection тут — заради
            спільного відступу між розділами. */}
        <PageSection as="div">
          <Eyebrow className="mb-7">Етап за етапом</Eyebrow>
          <LaunchStages stages={launchStages} />
        </PageSection>

        <InlineCta source="launch-plan-mid" />

        {/* Питання — розгортаннями поруч із заголовком: сітка 2×3 карток
            різної висоти «рвалася», а на телефоні шість відповідей поспіль
            займали два екрани. */}
        <section aria-labelledby="launch-faq-title" className="mt-14 lg:mt-20 grid gap-2 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <SectionTitle
                id="launch-faq-title"
                caption="Запитання"
                title="Часті запитання про відкриття клініки"
                link={{ to: '/pytannya', label: 'Усі запитання' }}
              />
            </div>
          </div>
          <div className="lg:col-span-8">
            <FaqList items={launchPlanCopy.faq} openFirst />
          </div>
        </section>
      </div>
    </PageShell>
  );
};

export default LaunchPlanPage;
